import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuRenderFn,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Paper from '@material-ui/core/Paper'
import { TextNode } from 'lexical'
import _ from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import COLORS from 'pared/constants/colors'

import { useVariables } from '../../variables'
import { SelectNode, createSelectNode } from './SelectNode'

interface IPropsType {
  options: string
}

class Option extends MenuOption {
  key: string
  metadata: {
    id: string
    parentId: string
    displayName: string
  }

  constructor(key: string, metadata: Option['metadata']) {
    super(key)
    this.key = key
    this.metadata = metadata
  }
}

const StyledPaper = styled(Paper)`
  position: absolute;
  top: 4px;
  left: 0px;
  max-height: 30vh;
  background: ${COLORS.background};
  overflow-y: scroll;
  z-index: 1600;
`

const SelectPlugin = (props: IPropsType) => {
  const [editor] = useLexicalComposerContext()
  const [queryString, setQueryString] = useState<string | null>(null)
  const { variables } = useVariables()
  const originalOptions = useMemo(
    () =>
      _.get(variables, props.options) as
        | {
            id: string
            parentId: string
            displayName: string
          }[]
        | undefined,
    [props, variables],
  )
  const options = useMemo(
    () =>
      !queryString
        ? []
        : (originalOptions || [])
            .filter((o) =>
              o.displayName
                .toUpperCase()
                .replace(/ > /g, '')
                .replace(/ /g, '')
                .includes(queryString.toUpperCase()),
            )
            .map((n) => new Option(n.id, n)),
    [queryString, originalOptions],
  )

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('@', {
    minLength: 0,
  })
  const checkForCustomDataTriggerMatch = useCallback(
    (text: string) => checkForTriggerMatch(text, editor),
    [checkForTriggerMatch, editor],
  )

  const onSelectOption = useCallback(
    (
      selectedOption: Option,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const customNode = createSelectNode(
          selectedOption.key,
          selectedOption.metadata,
        )

        if (nodeToReplace) nodeToReplace.replace(customNode)

        customNode.select()
        closeMenu()
      })
    },
    [editor],
  )

  const renderSuggestionsMenu = useCallback(
    (
      anchorElementRef: Parameters<MenuRenderFn<Option>>[0],
      {
        selectedIndex,
        selectOptionAndCleanUp,
        setHighlightedIndex,
      }: Parameters<MenuRenderFn<Option>>[1],
    ) =>
      anchorElementRef.current && options.length
        ? createPortal(
            <div>
              <StyledPaper>
                <MenuList>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.key}
                      onClick={() => {
                        setHighlightedIndex(index)
                        selectOptionAndCleanUp(option)
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(index)
                      }}
                      selected={index === selectedIndex}
                    >
                      {option.metadata.displayName}
                    </MenuItem>
                  ))}
                </MenuList>
              </StyledPaper>
            </div>,
            anchorElementRef.current,
          )
        : null,
    [options],
  )

  return (
    <LexicalTypeaheadMenuPlugin<Option>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForCustomDataTriggerMatch}
      options={options}
      menuRenderFn={renderSuggestionsMenu}
    />
  )
}

export { SelectNode }
export default SelectPlugin
